/**
 * OnboardingPlan — mandatory Stripe trial gate inserted right after OTP verification.
 *
 * New users land here before the onboarding wizard so we can capture a trial
 * commitment (no card required) before they invest time setting up a site.
 * The step is persistent — the only escape hatch is "Not ready? Log out".
 *
 * If the user already has an active trial or subscription (back-navigation,
 * refresh after webhook lands) they are forwarded straight to the wizard.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/lib/contexts/ThemeContext";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useBilling } from "@/hooks/useBilling";
import { track } from "@/lib/utils/track";
import { getAcquisitionContext } from "@/lib/utils/acquisitionContext";
import PlanExplorerDialog from "@/components/dashboard/hub/PlanExplorerDialog";

export default function OnboardingPlan() {
  const { setTheme } = useTheme();
  const { token } = useAuth();
  const billing = useBilling();
  const navigate = useNavigate();

  // Force light mode — restore on unmount
  useEffect(() => {
    const stored = localStorage.getItem("theme") as "light" | "dark" | null;
    setTheme("light");
    return () => {
      if (stored && stored !== "light") {
        setTheme(stored);
      }
    };
  }, []);

  // Track the impression once billing has loaded (avoids double-fire on re-renders).
  // Note: this is the Stripe billing gate, a distinct surface from the campaign
  // recs step — which owns `onboarding_plan_viewed` in the admin signup funnel.
  // Firing that event here would count billing-gate impressions as campaign-plan
  // views, so this leftover gate uses its own event name.
  useEffect(() => {
    if (billing.isLoading) return;
    track("onboarding_billing_gate_viewed", token, {
      ...getAcquisitionContext(),
    });
  }, [billing.isLoading]);

  // Forward users who already have a trial, subscription, or a pending workspace
  // invite — they don't need to set up billing before joining their team.
  useEffect(() => {
    if (billing.isLoading) return;
    if (billing.hasActiveTrial || billing.hasActiveSubscription) {
      navigate("/onboarding", { replace: true });
    } else if (billing.hasPendingInvites) {
      navigate("/dashboard", { replace: true });
    }
  }, [billing.isLoading, billing.hasActiveTrial, billing.hasActiveSubscription, billing.hasPendingInvites, navigate]);

  return (
    <PlanExplorerDialog
      open
      persistent
      context="onboarding"
      onOpenChange={() => {
        // persistent — no-op; only the logout escape hatch inside the dialog closes this
      }}
    />
  );
}
